export const GOOGLE_MAPS_API_KEY = (
  process.env.REACT_APP_GOOGLE_MAPS_API_KEY || ""
).trim();

const GOOGLE_MAPS_SCRIPT_ID = "google-maps-bootstrap-script";
let loadPromise = null;

function waitForImportLibrary() {
  return new Promise((resolve, reject) => {
    let attempts = 0;
    const timer = window.setInterval(() => {
      if (window.google?.maps?.importLibrary) {
        window.clearInterval(timer);
        resolve(window.google);
        return;
      }
      attempts += 1;
      if (attempts > 100) {
        window.clearInterval(timer);
        reject(new Error("Google Maps failed to initialize"));
      }
    }, 50);
  });
}

export function loadGoogleMapsPlaces() {
  if (typeof window === "undefined") {
    return Promise.reject(new Error("Google Maps is only available in the browser"));
  }

  if (window.google?.maps?.importLibrary) {
    return window.google.maps.importLibrary("places");
  }

  if (loadPromise) return loadPromise;

  if (!GOOGLE_MAPS_API_KEY) {
    return Promise.reject(new Error("Missing Google Maps API key"));
  }

  // Any failure clears the cached promise so the next city field retries instead of
  // showing the same error until the page is reloaded.
  const fail = (reject) => (error) => {
    loadPromise = null;
    reject(error);
  };

  loadPromise = new Promise((resolve, reject) => {
    const existing = document.getElementById(GOOGLE_MAPS_SCRIPT_ID);
    if (existing) {
      waitForImportLibrary()
        .then((google) => google.maps.importLibrary("places"))
        .then(resolve)
        .catch(fail(reject));
      return;
    }

    const script = document.createElement("script");
    script.id = GOOGLE_MAPS_SCRIPT_ID;
    script.src = `https://maps.googleapis.com/maps/api/js?key=${encodeURIComponent(
      GOOGLE_MAPS_API_KEY
    )}&loading=async`;
    script.async = true;
    script.defer = true;
    script.onload = () => {
      waitForImportLibrary()
        .then((google) => google.maps.importLibrary("places"))
        .then(resolve)
        .catch(fail(reject));
    };
    script.onerror = () => {
      // Remove the broken tag so a retry can inject a fresh one.
      script.remove();
      fail(reject)(new Error("Failed to load Google Maps JavaScript API"));
    };
    document.head.appendChild(script);
  });

  return loadPromise;
}

export function cityFromGooglePlace(place) {
  if (!place) return "";

  const components = place.addressComponents || place.address_components || [];
  const find = (type) => {
    const component = components.find((entry) => entry.types?.includes(type));
    return component?.longText || component?.long_name || component?.shortText || "";
  };

  return String(
    find("locality") ||
      find("administrative_area_level_2") ||
      find("administrative_area_level_3") ||
      ""
  ).trim();
}

export function placeLabelFromSelection(place, placePrediction) {
  const mainText = placePrediction?.mainText?.text || placePrediction?.mainText;
  if (mainText) return String(mainText).trim();

  const predictionText = placePrediction?.text?.text || placePrediction?.text;
  if (predictionText) {
    const raw = String(predictionText).trim();
    const pipeSegment = raw.split("|").pop();
    const cityCandidate = String(pipeSegment || raw).trim();
    const commaSegment = cityCandidate.split(",").map((part) => part.trim()).filter(Boolean).pop();
    return commaSegment || cityCandidate || raw;
  }

  const formatted = place?.formattedAddress || place?.formatted_address;
  if (formatted) return String(formatted).trim();

  const display = place?.displayName || place?.name;
  if (display) return String(display).trim();

  return cityFromGooglePlace(place);
}
